'use client'

import { memo, useEffect, useRef, useState } from 'react'
import { Button, Divider, EmptyState, Loader, Spacer, Image, Cell } from '@vinted/web-ui'
import { isEmpty } from 'lodash'

import { Advertisement } from '@marketplace-web/ads/ads-feature'
import { AdShape } from '@marketplace-web/ads/ads-data'
import { ShippingFeesAppliedBanner } from '@marketplace-web/escrow-pricing/escrow-pricing-feature'
import {
  SearchSubscribeButton,
  useSavedSearchesContext,
  useSavedSearchesFlow,
} from '@marketplace-web/search/search-bar'
import {
  CatalogNavigationModel,
  CatalogFeature,
  logCatalogError,
  CatalogState,
  CatalogMvBannerDto,
} from '@marketplace-web/catalog/catalog-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useLocation } from '@marketplace-web/shared/browser'
import { FormattedMessage } from '@marketplace-web/i18n/i18n-feature'
import { GoogleTagManagerEvent, googleTagManagerTrack } from '@marketplace-web/domain/google-track'
import { useSession } from '@marketplace-web/shared/session'
import { ErrorBoundary } from '@marketplace-web/shared/error-display'
import { UiState, useIsMounted } from '@marketplace-web/shared/ui-state'
import { BrandModel } from '@marketplace-web/personalization/brands-data'

import { getSearchSessionData } from '@marketplace-web/search/search'
import {
  clickCatalogBack,
  CatalogProvider,
  useCatalogContext,
  CatalogBrandReducerAction,
} from '@marketplace-web/catalog/catalog-page-data'
import { useChatkit } from '@marketplace-web/search/chatkit'

import {
  useLogViewCategoryEvent,
  useLogSearchedBrandEvent,
} from '@marketplace-web/braze/braze-feature'
import {
  ProactiveModeration,
  ProactiveModerationContextProvider,
} from '@marketplace-web/domain/admin-alerts'

import { clickEvent, viewEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { ITEM_UPLOAD_URL } from 'constants/routes'

import { incrementCatalogLoad } from './utils/observability'
import Filters from './components/filters/Filters'
import SelectedFilters from './components/filters/SelectedFilters'
import MobileFilters from './components/filters/MobileFilters'
import SubcatalogNavigationFaceted from './components/filters/SubcatalogNavigationFaceted'
import Breadcrumbs from './components/listing/Breadcrumbs'
import Title from './components/listing/Title'
import Items from './components/listing/Items'
import Marketing from './components/listing/Marketing'
import Pagination from './components/listing/Pagination'
import ResultRanking from './components/listing/ResultRanking'
import useFetchCatalogData from './hooks/useFetchCatalogData'
import useHandleCatalogSearchTextChange from './hooks/useHandleCatalogSearchTextChange'
import useItemsTracking from './hooks/useItemsTracking'

export const STICKY_BANNER_SPACING = 20

type Props = {
  learnMoreUrl: string | null
  isBot?: boolean
  brand?: BrandModel
  catalog?: CatalogNavigationModel
  mvBanner?: CatalogMvBannerDto
  // eslint-disable-next-line react/no-unused-prop-types
  initialCatalogState?: CatalogState
}

const handleErrorLogging = (feature?: CatalogFeature) => (err: Error | null) => {
  incrementCatalogLoad('error', 'error_boundary')
  logCatalogError(err, feature)
}

const Catalog = ({ learnMoreUrl, isBot, brand, catalog, mvBanner }: Props) => {
  const {
    dispatch,
    state: {
      data: { uiState, catalogTree },
      general: { filtersChangeCount },
      filters: { catalogIds },
      dynamicFilters: { selectedDynamicFilters },
      items,
      searchSession,
    },
  } = useCatalogContext()
  const trackItems = useItemsTracking()
  const fetchCatalogData = useFetchCatalogData(trackItems)
  const breakpoints = useBreakpoint()
  const { relativeUrl, searchParams } = useLocation()
  const { user } = useSession()
  const { track } = useTracking()
  const [offset, setOffset] = useState(0)
  const isCatalogServerSideFetchingEnabled =
    useAbTest('web_initial_server_side_loading')?.variant === 'on'

  const userId = user?.id
  const isModerator = user?.is_catalog_moderator
  const isRoleMarketingPhotos = user?.is_catalog_role_marketing_photos
  const replicaWarning = items?.replicaWarning

  const { actions: savedSearchesActions, currentSearchId } = useSavedSearchesContext()

  useSavedSearchesFlow()
  useLogViewCategoryEvent(catalogIds[0], catalogTree)
  useLogSearchedBrandEvent(brand?.id || searchParams.brand_ids)

  const isSendToAlertsShown = isRoleMarketingPhotos || isModerator
  const searchText = searchParams.search_text ? String(searchParams.search_text) : undefined

  const initialSearchSet = useRef(false)

  const filtersChangeCountRef = useRef(filtersChangeCount)
  filtersChangeCountRef.current = filtersChangeCount

  const selectedDynamicFiltersRef = useRef(selectedDynamicFilters)
  selectedDynamicFiltersRef.current = selectedDynamicFilters

  const searchSessionRef = useRef(searchSession)
  searchSessionRef.current = searchSession

  const chatkit = useChatkit()

  useTrackAbTest(mvBanner?.experiment)

  useHandleCatalogSearchTextChange()

  const isMounted = useIsMounted()

  useEffect(() => {
    const cb = () => {
      track(clickCatalogBack(getSearchSessionData()))
    }

    window.addEventListener('pagehide', cb)

    return () => {
      window.removeEventListener('pagehide', cb)
    }
  }, [track])

  useEffect(() => {
    if (initialSearchSet.current || filtersChangeCount === 0 || currentSearchId || !userId) return
    initialSearchSet.current = true

    if (catalogIds[0] || searchText) {
      savedSearchesActions.createSearch({
        userId,
        search: {
          search_text: searchText || undefined,
          catalog_id: catalogIds[0],
          subscribed: false,
        },
      })
    }
  }, [catalogIds, currentSearchId, filtersChangeCount, savedSearchesActions, userId, searchText])

  useEffect(() => {
    // calcute the offset for sticky banner when the client code is loaded
    const headerHeight = document.querySelector<HTMLDivElement>('.js-header')?.offsetHeight || 0

    setOffset(headerHeight + STICKY_BANNER_SPACING)
  }, [])

  useEffect(() => {
    fetchCatalogData({ relativeUrl, searchParams })

    if (isCatalogServerSideFetchingEnabled) trackItems(items)

    dispatch({ type: CatalogBrandReducerAction.SET_CATALOG_BRAND, payload: { brand } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!catalog) return

    googleTagManagerTrack(GoogleTagManagerEvent.ViewCatalog, {
      catalog_id: catalog.id,
      catalog_code: catalog.code,
      catalog_name: catalog.title,
    })
  }, [catalog])

  useEffect(() => {
    if (mvBanner?.banner) {
      track(
        viewEvent({
          target: 'mv_visibility_banner',
          screen: Screen.Catalog,
        }),
      )
    }
  }, [mvBanner, track])

  const handleRetryCta = () => {
    fetchCatalogData({ relativeUrl, searchParams })
  }

  const handleBannerClick = () => {
    track(
      clickEvent({
        target: ClickableElement.MvVisibilityBanner,
        screen: Screen.Catalog,
      }),
    )
  }

  const renderFilters = () => {
    return (
      <ErrorBoundary
        FallbackComponent={ErrorBoundary.ComponentError}
        preventLog
        onError={handleErrorLogging(CatalogFeature.Filters)}
      >
        {isEmpty(replicaWarning) && (
          <>
            <Filters learnMoreUrl={learnMoreUrl} />
            <Spacer size={Spacer.Size.Small} />
            <SelectedFilters />
            <Spacer size={Spacer.Size.Small} />
            <SubcatalogNavigationFaceted isBot={isBot} />
            <Divider />
            <ResultRanking learnMoreUrl={learnMoreUrl} />
          </>
        )}
      </ErrorBoundary>
    )
  }

  const renderMobileFilters = () => {
    return (
      <ErrorBoundary
        FallbackComponent={ErrorBoundary.ComponentError}
        preventLog
        onError={handleErrorLogging(CatalogFeature.MobileFilters)}
      >
        {isEmpty(replicaWarning) && (
          <>
            <div className="u-flexbox">
              <div className="u-flexbox u-flex-direction-column u-margin-right-auto">
                <Spacer />
              </div>
              <div className="u-flexbox u-align-items-flex-start">
                <MobileFilters learnMoreUrl={learnMoreUrl} />
              </div>
            </div>
            <ResultRanking learnMoreUrl={learnMoreUrl} />
          </>
        )}
      </ErrorBoundary>
    )
  }

  const renderTitleBlock = () => {
    return (
      <div className="u-flexbox">
        <div className="u-margin-right-auto">
          <div>
            <div className="u-inline-block u-ui-margin-right-regular">
              <Title />
            </div>
          </div>
          <div className="u-ui-margin-top-small" />
        </div>
        <div className="u-tablets-up-only u-flexbox u-align-items-flex-end">
          <SearchSubscribeButton catalogTree={catalogTree} />
        </div>
      </div>
    )
  }

  const renderHeader = () => {
    const headerBlock = (
      <nav>
        <Breadcrumbs truncate />
      </nav>
    )

    if (breakpoints.phones) {
      return (
        <>
          {headerBlock}
          <Title truncate />
          <Spacer />
          {renderMobileFilters()}
        </>
      )
    }

    return (
      <>
        {headerBlock}
        {renderTitleBlock()}
        <Spacer size={Spacer.Size.Medium} />
        <Divider />
        <Spacer size={Spacer.Size.Medium} />
        {renderFilters()}
      </>
    )
  }

  const renderCatalogMvBanner = (url: string, description: string, id: string) => (
    <div
      className={id === 'desktop' ? 'u-sticky' : ''}
      style={id === 'desktop' ? { top: offset } : {}}
    >
      <Cell
        url={ITEM_UPLOAD_URL}
        testId={`catalog-mv-banner-${id}`}
        styling={Cell.Styling.Tight}
        onClick={handleBannerClick}
        body={
          <div className="u-flexbox u-justify-content-center u-margin-auto catalog-mv-banner">
            <Image role="img" src={url} alt={description} scaling={Image.Scaling.Contain} />
          </div>
        }
      />
      <Spacer size={Spacer.Size.Medium} />
    </div>
  )

  const renderCatalog = () => {
    if (uiState === UiState.Pending) {
      return (
        <div className="u-flexbox u-justify-content-center">
          <Loader testId="catalog-page-loader" />
        </div>
      )
    }

    if (uiState === UiState.Failure) {
      return (
        <EmptyState
          title={<FormattedMessage id="catalog.generic_error.title" />}
          body={<FormattedMessage id="catalog.generic_error.body" />}
          action={
            <Button
              text={<FormattedMessage id="catalog.generic_error.cta" />}
              styling={Button.Styling.Filled}
              onClick={handleRetryCta}
            />
          }
        />
      )
    }

    return (
      <>
        {renderHeader()}
        <ProactiveModeration
          isSendToAlertsShown={!!isSendToAlertsShown}
          isRoleMarketingPhotos={!!isRoleMarketingPhotos}
          marketingSection={<Marketing />}
        />
        <ShippingFeesAppliedBanner prefix={<Spacer size={Spacer.Size.Medium} />} />
        <Spacer size={breakpoints.phones ? Spacer.Size.Small : Spacer.Size.Medium} />
        <ErrorBoundary
          FallbackComponent={ErrorBoundary.ComponentError}
          preventLog
          onError={handleErrorLogging(CatalogFeature.Items)}
        >
          <Items mvBanner={mvBanner} />
          <Pagination />
        </ErrorBoundary>
      </>
    )
  }

  return (
    <>
      {mvBanner?.banner && !breakpoints.desktops
        ? renderCatalogMvBanner(mvBanner.banner.url, mvBanner.banner.description, 'mobile')
        : !mvBanner?.banner &&
          !chatkit.isOpen && <Advertisement shape={AdShape.Leaderboard} forceAdCheck />}
      <div className="row content-wrapper-flex">
        <section className="content-container">{renderCatalog()}</section>
        <aside id="sidebar" className="sidebar-container">
          {!chatkit.isOpen &&
            (mvBanner?.banner && isMounted() && breakpoints.desktops
              ? renderCatalogMvBanner(mvBanner.banner.url, mvBanner.banner.description, 'desktop')
              : !mvBanner?.banner && (
                  <>
                    <Advertisement shape={AdShape.Rectangle} isSidebarAd forceAdCheck />
                    <Advertisement shape={AdShape.Skyscraper} isSidebarAd forceAdCheck />
                  </>
                ))}
          <div
            ref={chatkit.chatkitRef}
            style={{
              display: chatkit.isOpen ? undefined : 'none',
              position: 'sticky',
              top: 100,
              width: '100%',
              height: '100%',
              maxHeight: '80vh',
              padding: '12px',
            }}
          />
        </aside>
      </div>
    </>
  )
}

const CatalogWithErrorBoundary = (props: Props) => (
  <ErrorBoundary
    FallbackComponent={ErrorBoundary.ComponentError}
    preventLog
    onError={handleErrorLogging()}
  >
    <CatalogProvider initialCatalogState={props.initialCatalogState}>
      <ProactiveModerationContextProvider>
        <Catalog {...props} />
      </ProactiveModerationContextProvider>
    </CatalogProvider>
  </ErrorBoundary>
)

export default memo(CatalogWithErrorBoundary)
